import { Search, ChevronDown, Menu, Building2, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useState, useRef, useEffect, useId } from "react";
import { AddMenu } from "@/components/add/AddMenu";
import { useAuth } from "@/lib/auth";
import { useLocation } from "wouter";
import { NotificationsPopover } from "@/components/NotificationsPopover";
import { NewReminderButton } from "@/components/reminders/NewReminderButton";
import { ThemeToggleSimple as ThemeToggle } from "@/components/ThemeToggle";
import { useGlobalSearch, type SearchResult } from "@/hooks/useGlobalSearch";
import { QueryError } from "@/components/QueryError";

export function Header({
  title,
  onMenuClick,
  onSearchClick,
}: {
  title: string;
  onMenuClick?: () => void;
  onSearchClick?: () => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(-1);
  const { results, isSearching, isError, error, refetch, isFetching } =
    useGlobalSearch(query);
  const { user, logout } = useAuth();
  const [, navigate] = useLocation();
  const container = useRef<HTMLDivElement>(null);
  const listId = useId();
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!container.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useEffect(() => {
    setSelected(-1);
  }, [query]);
  useEffect(() => {
    container.current
      ?.querySelector(`[data-result-index="${selected}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [selected]);
  const choose = (result: SearchResult) => {
    setOpen(false);
    setQuery("");
    navigate(result.href);
  };
  return (
    <header
      className="min-h-14 md:min-h-16 bg-card border-b border-card-border px-3 md:px-6 py-2 flex flex-wrap items-center gap-3 sticky top-0 z-40 shadow-sm"
      data-testid="header"
    >
      <div className="flex items-center gap-2 min-w-0 flex-1 md:max-w-[40%]">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden shrink-0"
          onClick={onMenuClick}
          aria-label="Open navigation"
          data-testid="mobile-menu-button"
        >
          <Menu />
        </Button>
        <h1
          className="text-lg md:text-xl font-bold truncate"
          title={title}
          data-testid="page-title"
        >
          {title}
        </h1>
      </div>
      <div
        ref={container}
        className="relative hidden lg:block flex-1 min-w-[180px] max-w-xl"
      >
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          type="search"
          role="combobox"
          aria-label="Search all companies and contacts"
          aria-expanded={open && query.trim().length >= 2}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            selected >= 0 && results[selected]
              ? `${listId}-${selected}`
              : undefined
          }
          placeholder="Search companies, contacts…"
          value={query}
          className="pl-10 bg-muted/50"
          data-testid="global-search"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
              setSelected((i) => Math.min(i + 1, results.length - 1));
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setSelected((i) => Math.max(i - 1, 0));
            }
            if (
              e.key === "Enter" &&
              open &&
              !isSearching &&
              !isError &&
              query.trim().length >= 2 &&
              results[selected]
            ) {
              e.preventDefault();
              choose(results[selected]);
            }
            if (e.key === "Escape") setOpen(false);
          }}
        />
        {open && query.trim().length >= 2 && (
          <div
            className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-lg shadow-lg overflow-y-auto max-h-[min(400px,60dvh)]"
            data-testid="search-dropdown"
          >
            {isError ? (
              <QueryError
                error={error}
                onRetry={() => refetch()}
                busy={isFetching}
                noun="search results"
              />
            ) : isSearching ? (
              <p role="status" className="p-4 text-sm">
                Searching…
              </p>
            ) : results.length === 0 ? (
              <p role="status" className="p-4 text-sm">
                No results found
              </p>
            ) : (
              <div role="listbox" id={listId} aria-label="Search results">
                {results.map((r, i) => (
                  <button
                    key={`${r.type}-${r.id}`}
                    type="button"
                    role="option"
                    aria-selected={selected === i}
                    id={`${listId}-${i}`}
                    data-result-index={i}
                    className={`w-full flex items-center gap-3 p-3 text-left hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring ${selected === i ? "bg-muted" : ""}`}
                    onClick={() => choose(r)}
                  >
                    {r.type === "contact" ? (
                      <Users className="w-4 h-4 shrink-0" />
                    ) : (
                      <Building2 className="w-4 h-4 shrink-0" />
                    )}
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">
                        {r.name}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {r.subtitle}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          aria-label="Search companies and contacts"
          onClick={onSearchClick}
        >
          <Search />
        </Button>
        <div className="hidden sm:block">
          <AddMenu />
        </div>
        <div className="hidden sm:block">
          <NewReminderButton />
        </div>
        <NotificationsPopover />
        <div className="hidden sm:block">
          <ThemeToggle />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-9 gap-1 px-2 hidden sm:inline-flex"
              data-testid="user-menu"
            >
              <span className="max-w-[8rem] truncate">
                {user?.name || "Account"}
              </span>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => navigate("/settings")}
              data-testid="menu-settings"
            >
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="menu-logout">
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
